// @flow
import * as React from 'react';
import { connect } from 'react-redux';
import _ from 'lodash';
// import type { Dispatch } from 'redux';

import { dataLayerEvents } from 'common/utils/analytics';
import { getAverageGrades, getCities, getStudies } from 'client/actions';
import type { Store } from 'client/reducers';
import type { AverageGradesOptions as Options } from 'api';
import Filter from './Filter';
import Graph from './Graph';
import Details from './Details';
import css from './Comparison.scss';

// Comparison graph:
// grey - national grades avg by [year, year..], [study(aka. field)]
// color - city grades avg for [year, year ..] by [study(field)]

type Props = {
	getAverageGrades: (options: Options) => Promise<any>,
	getStudies: () => Promise<any>,
	// getCities: () => Promise<any>,
};

type FilterData = {
	study: string,
	city: Array<string>,
};

function check(data) {
	const { year, citiesAvg, city: cities, nationalAvg: national } = data;

	const yearLen = year.length;
	const errors = [];

	const checkTotal = total => {
		const full = total === 0;
		const partial = total > 0 && total < yearLen;
		const result = full || partial;
		return {
			full,
			partial,
			result,
		};
	};

	cities.forEach((city, index) => {
		const total = Object.values(citiesAvg).filter(o => _.get(o, 'city') === city).length;
		const isMiss = checkTotal(total);
		if (isMiss.result) {
			errors.push({ city, index, isMiss });
		}
	});

	const total = Object.values(national).length;
	const isMiss = checkTotal(total);
	if (isMiss.result) {
		errors.push({ isMiss });
	}

	return errors;
}

const Comparison = (props: Props) => {
	const years = ['2016', '2017', '2018', '2019', '2020'];
	const [options, setOptions] = React.useState(null);
	const [selectedOptions, setSelectedOptions] = React.useState(null);
	const [data, setData] = React.useState(null);
	const appRef = React.useRef(null);

	const onFilterChange = React.useCallback((filterData: FilterData) => {
		setSelectedOptions({ ...filterData, year: years });
	}, []);

	const getOptions = async () => {
		// cities can vary depending on selected study field, and will be updated on each getData()
		// const cities = await props.getCities();
		const studies = await props.getStudies();

		const omitStudies = [];
		_.forEach(studies, el => {
			if (el !== 'דת האסלם 2') {
				omitStudies.push(el);
			}
		});

		setOptions({ city: {}, study: omitStudies });
	};

	const getData = async () => {
		if (selectedOptions) {
			const averageGrades = await props.getAverageGrades(selectedOptions);

			setOptions(prevOptions => ({ ...prevOptions, city: averageGrades.cities }));

			setData({
				...averageGrades,
				city: selectedOptions.city,
				study: selectedOptions.study,
				year: years,
			});
		}
	};

	const comparisonRef = React.useRef(null);
	const observer = React.useCallback(() => {
		const height = appRef.current.offsetHeight;
		const parentHeight = window.innerHeight;
		if (height !== parentHeight && Math.abs(height - parentHeight) > 5) {
			window.parent.postMessage(
				{
					payload: { iframeHeight: height },
					source: 'comparison-graph',
					type: 'resize',
				},
				'*'
			);
		}
		requestAnimationFrame(observer);
	}, [comparisonRef]);

	React.useEffect(() => {
		dataLayerEvents.onComparison();
		if (!appRef.current) {
			appRef.current = document.getElementById('app');
		}
		requestAnimationFrame(observer);
		getOptions();
		return () => {
			cancelAnimationFrame(observer);
		};
	}, []);

	React.useEffect(() => {
		getData();
	}, [selectedOptions]);

	return (
		<div className={css.comparison} ref={comparisonRef}>
			{options && (
				<Filter
					onChange={onFilterChange}
					study={selectedOptions ? selectedOptions.study : ''}
					studies={options.study}
					cities={options.city}
				/>
			)}
			{data && (
				<React.Fragment>
					{check(data).map(error => {
						const color = error.city
							? Graph.colors.cities[data.city.indexOf(error.city)]
							: Graph.colors.national;
						const strMain = 'נתונים חסרים ל';
						const nationalAvgTitle = 'ממוצע ארצי';
						return (
							<div className={css.error} key={`error-${color}`}>
								{strMain}
								<b style={{ color }}>{error.city || nationalAvgTitle}</b>
							</div>
						);
					})}
					<Graph
						citiesAvg={data.citiesAvg}
						nationalAvg={data.nationalAvg}
						years={data.year}
						cities={data.city}
					/>
					<Details
						study={data.study}
						years={data.year}
						cities={data.city}
						citiesAvg={data.citiesAvg}
						nationalAvg={data.nationalAvg}
					/>
				</React.Fragment>
			)}
		</div>
	);
};

const mapState = (store: Store): Props => ({});

const mapDispatch = {
	getAverageGrades,
	getCities,
	getStudies,
};

export default (connect(mapState, mapDispatch)(Comparison): React.AbstractComponent<{}>);
